import { useEffect, useRef, useState, type ReactNode } from "react"
import {
  Pencil,
  GitMerge,
  Archive,
  RotateCcw,
  Check,
  MousePointerClick,
  Shield,
  Calendar,
  Lock,
  Info,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Badge } from "@/components/ui/badge"
import { fmtCompactRelative } from "@/lib/format"
import { EscalationReasonStatusBadge } from "./escalation-reason-status-badge"
import type { EscalationReason } from "@/types/agent_escalation_reason"

const DESCRIPTION_SOFT_LIMIT = 300

type Props = {
  reason: EscalationReason | null
  onApprove:        (r: EscalationReason) => void
  onArchive:        (r: EscalationReason) => void
  onRestore:        (r: EscalationReason) => void
  onMerge:          (r: EscalationReason) => void
  onRename:         (r: EscalationReason, name: string) => void
  onEditDescription:(r: EscalationReason, description: string) => void
}

export function EscalationReasonEditPanel(props: Props) {
  if (props.reason === null) return <EmptyState />
  // Re-mount the editor when the selected row changes so the local
  // draft state for name/description resets cleanly.
  return <EditPanelInner key={props.reason.id} {...props} reason={props.reason} />
}

function EditPanelInner({
  reason,
  onApprove,
  onArchive,
  onRestore,
  onMerge,
  onRename,
  onEditDescription,
}: Props & { reason: EscalationReason }) {
  const [name, setName] = useState(reason.name)
  const [description, setDescription] = useState(reason.description)
  const nameRef = useRef<HTMLInputElement | null>(null)

  // Sync local drafts when the underlying row updates from the server
  // (after a PATCH triggered by another field's blur).
  useEffect(() => { setName(reason.name) }, [reason.name])
  useEffect(() => { setDescription(reason.description) }, [reason.description])

  const isCatchAll = reason.key === "unclassified"

  const commitName = () => {
    const next = name.trim()
    if (!next) { setName(reason.name); return }
    if (next === reason.name) return
    onRename(reason, next)
  }
  const commitDesc = () => {
    const next = description.trim()
    if (next === reason.description) return
    onEditDescription(reason, next)
  }

  const provenanceText =
    reason.status === "archived"
      ? `archived ${fmtCompactRelative(reason.updatedAt)}`
      : reason.provenance === "human" && reason.createdByName
        ? `by ${reason.createdByName} · ${fmtCompactRelative(reason.createdAt)}`
        : `classifier · ${fmtCompactRelative(reason.createdAt)}`

  return (
    <div className="space-y-6 p-6">
      <header>
        <h2 className="text-lg font-semibold text-app-text">Edit reason</h2>
        <p className="mt-1 text-sm text-app-muted">
          Update details, behavior, and actions for this escalation reason.
        </p>
      </header>

      <Section number={1} title="Basics">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label className="text-xs uppercase tracking-wide text-app-muted">Name</label>
            <Input
              ref={nameRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={commitName}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); (e.target as HTMLInputElement).blur() }
                if (e.key === "Escape") { setName(reason.name); (e.target as HTMLInputElement).blur() }
              }}
              className="w-full"
            />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs uppercase tracking-wide text-app-muted">Key (slug)</label>
              <Badge tone="success" className="gap-1 px-2 py-0.5 text-[10px]">
                <Lock size={10} strokeWidth={2.5} /> Locked
              </Badge>
            </div>
            <Input
              type="text"
              value={reason.key}
              disabled
              readOnly
              className="w-full font-mono"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <label className="text-xs uppercase tracking-wide text-app-muted">Description</label>
          <div className="relative">
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={commitDesc}
              onKeyDown={(e) => {
                if (e.key === "Escape") { setDescription(reason.description); (e.target as HTMLTextAreaElement).blur() }
              }}
              rows={4}
              placeholder="1-3 sentences. Used by the classifier prompt to decide if this reason fits."
              className="pb-7"
            />
            <div className="pointer-events-none absolute bottom-2 right-3 text-[11px] text-app-subtle tabular-nums">
              {description.length} / {DESCRIPTION_SOFT_LIMIT}
            </div>
          </div>
        </div>
      </Section>

      <Section number={2} title="Behavior">
        <div className="grid gap-4 sm:grid-cols-2">
          <Stat label="Provenance" value={provenanceText} />
          <Stat label="Used" value={`${reason.timesUsed} ${reason.timesUsed === 1 ? "time" : "times"}`} />
        </div>
        <div className="flex items-start gap-2 rounded-control border border-border bg-app-panel3 px-3 py-2.5 text-xs text-app-muted">
          <Info size={14} className="mt-0.5 shrink-0 text-app-subtle" />
          <span>
            {reason.provenance === "human"
              ? "Operator-created reasons can be renamed, merged, or archived at any time."
              : "Classifier-generated reasons can be renamed or merged but not removed."}
          </span>
        </div>
      </Section>

      <Section number={3} title="Actions">
        <div className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-control border border-border bg-app-panel2/40 px-4 py-3 sm:grid-cols-4">
          <Stat label="Used" value={String(reason.timesUsed)} />
          <Stat
            label="Last classified"
            value={
              <span className="inline-flex items-center gap-1.5">
                {fmtCompactRelative(reason.lastUsedAt)}
                <Calendar size={12} className="text-app-subtle" />
              </span>
            }
          />
          <Stat label="Status" value={<EscalationReasonStatusBadge status={reason.status} />} />
          <Stat
            label="Protected catch-all"
            value={
              <span className="inline-flex items-center gap-1.5">
                {isCatchAll ? "Yes" : "No"}
                <Shield size={12} className={isCatchAll ? "text-vip-gold" : "text-app-subtle"} />
              </span>
            }
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {reason.status === "pending" && (
            <Button variant="red" size="sm" onClick={() => onApprove(reason)}>
              <Check size={14} className="mr-1.5" />
              Approve
            </Button>
          )}
          {(reason.status === "live" || reason.status === "pending") && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => nameRef.current?.focus()}
              title="Edit the name field above"
            >
              <Pencil size={14} className="mr-1.5" />
              Rename
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => onMerge(reason)}>
            <GitMerge size={14} className="mr-1.5" />
            Merge into…
          </Button>
          {reason.status === "archived" ? (
            <Button variant="ghost" size="sm" onClick={() => onRestore(reason)}>
              <RotateCcw size={14} className="mr-1.5" />
              Restore
            </Button>
          ) : (
            <Button
              variant="red"
              size="sm"
              disabled={isCatchAll}
              onClick={() => onArchive(reason)}
              title={isCatchAll ? "Unclassified is the synchronous default for newly-escalated convos and cannot be archived." : undefined}
            >
              <Archive size={14} className="mr-1.5" />
              Archive
            </Button>
          )}
        </div>
      </Section>
    </div>
  )
}

function Section({ number, title, children }: { number: number; title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <header className="flex items-center gap-2">
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-app-panel3 text-[11px] font-semibold text-app-muted">
          {number}
        </span>
        <h3 className="text-base font-semibold text-app-text">{title}</h3>
      </header>
      {children}
    </section>
  )
}

function Stat({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="text-[11px] uppercase tracking-wide text-app-muted">{label}</div>
      <div className="text-sm text-app-text">{value}</div>
    </div>
  )
}

function EmptyState() {
  return (
    <div className="flex h-full min-h-[420px] flex-col items-center justify-center gap-3 px-6 py-10 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-card bg-white/5 text-app-muted">
        <MousePointerClick size={20} />
      </div>
      <div className="text-sm font-medium text-app-text">Select a reason to edit</div>
      <p className="max-w-xs text-xs text-app-muted">
        Pick any row from the catalog to rename, edit its description, merge, or archive it.
      </p>
    </div>
  )
}
